import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CandidatesFilters } from '../components/CandidatesFilters';
import { CandidatesList } from '../components/CandidatesList';
import { useCandidates, useCandidatesStats } from '../hooks';
import type { CandidateFilters, PaginationParams } from '../types';

export function CandidatesPage() {
  const [filters, setFilters] = useState<CandidateFilters>({});
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 12,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data, isLoading, error } = useCandidates(filters, pagination);
  const { data: stats } = useCandidatesStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Candidats</h1>
          <p className="text-muted-foreground">
            Gérez votre base de candidats
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/itc/search">
            <Button variant="outline">
              <Sparkles className="mr-2 h-4 w-4" />
              Recherche IA
            </Button>
          </Link>
          <Link to="/itc/candidates/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Nouveau candidat
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total candidats</CardDescription>
            <CardTitle className="text-2xl">{stats?.total ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Disponibles immédiatement</CardDescription>
            <CardTitle className="text-2xl text-green-600">{stats?.availableNow ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Ajoutés ce mois</CardDescription>
            <CardTitle className="text-2xl">{stats?.addedThisMonth ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>CVs à traiter</CardDescription>
            <CardTitle className="text-2xl text-orange-600">{stats?.withoutEmbedding ?? 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <CandidatesFilters filters={filters} onFiltersChange={(f) => {
        setFilters(f);
        setPagination((p) => ({ ...p, page: 1 }));
      }} />

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              Erreur lors du chargement des candidats. Veuillez réessayer.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Candidates List */}
      <CandidatesList candidates={data?.data} isLoading={isLoading} />

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {data.pagination.page} sur {data.pagination.totalPages} ({data.pagination.total} candidats)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: (p.page || 1) - 1 }))}
            >
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.pagination.page >= data.pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: (p.page || 1) + 1 }))}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
